import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BarChart3,
  Mails,
  Clock,
  AlertCircle,
  CheckCircle2,
  Eye,
  FileBarChart,
  Info,
  Send,
  Mail,
  MessageSquare,
} from 'lucide-react'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useCampaniasEnviadas } from '@/modules/multichannel/hooks/useCampanias'
import type { Campania } from '@/modules/multichannel/types/campania'
import { useAuth } from '@/auth'
import { canUpdateReports } from '@/shared/lib/utils'
import { ReporteModal } from '@/modules/cronograma/components/ReporteModal'
import { ReporteWhatsAppModal } from '@/modules/cronograma/components/ReporteWhatsAppModal'

// Tipos de campaña que incluyen cada canal (mismo criterio que CampaniasSeguimiento)
const WHATSAPP_TIPOS = ['Whatsapp', 'Email + Whatsapp']
const EMAIL_TIPOS = ['Email', 'Email + Whatsapp']

function getReportTypes(tipo: string): { showEmail: boolean; showWhatsApp: boolean } {
  return {
    showEmail: EMAIL_TIPOS.includes(tipo),
    showWhatsApp: WHATSAPP_TIPOS.includes(tipo),
  }
}

// Estados de reporte inferidos (misma lógica que el legacy campanias_analytics.html)
type EstadoKey = 'generado' | 'recopilando' | 'por_generar' | 'sin_estado'

const ESTADO_CONFIG: Record<EstadoKey, { label: string; badge: string }> = {
  generado: {
    label: 'Generado',
    badge: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
  },
  recopilando: {
    label: 'Recopilando información',
    badge: 'bg-sky-500/10 text-sky-600 border-sky-500/30 dark:text-sky-400',
  },
  por_generar: {
    label: 'Reporte por generar',
    badge: 'bg-amber-500/10 text-amber-600 border-amber-500/30 dark:text-amber-400',
  },
  sin_estado: {
    label: 'Sin estado',
    badge: 'bg-gray-400/10 text-gray-500 border-gray-400/30 dark:text-gray-400',
  },
}

// Días transcurridos desde el envío (fecha_publicacion + hora), sin desfase de zona horaria
function diasDesdeEnvio(campania: Campania): number {
  if (!campania.fecha_publicacion) return 0
  const [year, month, day] = campania.fecha_publicacion.split('-').map(Number)
  const [hh, mm] = (campania.hora || '00:00').split(':').map(Number)
  const envio = new Date(year, month - 1, day, hh || 0, mm || 0)
  const diff = Date.now() - envio.getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

function getEstadoReporte(campania: Campania): EstadoKey {
  const estado = (campania.estado_reporte || '').toLowerCase()
  if (estado.includes('generado') || estado.includes('completado')) return 'generado'
  const dias = diasDesdeEnvio(campania)
  if (dias >= 1 && dias <= 3) return 'recopilando'
  if (dias > 3) return 'por_generar'
  return 'sin_estado'
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-'
  const [year, month, day] = dateString.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return new Intl.DateTimeFormat('es-EC', { year: 'numeric', month: 'short', day: 'numeric' }).format(date)
}

const STAT_CARDS = [
  { key: 'total', label: 'Total Enviadas', icon: Mails, hex: '#8d82bc' },
  { key: 'recopilando', label: 'Recopilando Información', icon: Clock, hex: '#57a6d8' },
  { key: 'por_generar', label: 'Reporte por Generar', icon: AlertCircle, hex: '#f6c23e' },
  { key: 'generado', label: 'Reportes Generados', icon: CheckCircle2, hex: '#2fbc80' },
] as const

export function CampaniasAnalytics() {
  useDocumentTitle('Analytics de Campañas')
  const navigate = useNavigate()
  const { user } = useAuth()
  const showActualizado = canUpdateReports(user)

  const [estadoFilter, setEstadoFilter] = useState<'todos' | EstadoKey>('todos')
  const [marcaFilter, setMarcaFilter] = useState<string>('todas')
  const [actualizadoFilter, setActualizadoFilter] = useState<'todos' | 'actualizado' | 'pendiente'>('todos')
  const [showInfo, setShowInfo] = useState(false)

  // Edición de reportes (modales, igual que en CampaniasSeguimiento)
  const [selectedCampania, setSelectedCampania] = useState<Campania | null>(null)
  const [reporteEmailModalOpen, setReporteEmailModalOpen] = useState(false)
  const [reporteWhatsAppModalOpen, setReporteWhatsAppModalOpen] = useState(false)

  const handleEditReport = (campania: Campania) => {
    setSelectedCampania(campania)
    setReporteEmailModalOpen(true)
  }

  const handleEditWhatsAppReport = (campania: Campania) => {
    setSelectedCampania(campania)
    setReporteWhatsAppModalOpen(true)
  }

  const { data, isLoading } = useCampaniasEnviadas({ orden: '-fecha_publicacion' })

  // Solo campañas enviadas (con fecha de publicación), igual que el legacy
  const campaniasEnviadas = useMemo(
    () => (data || []).filter((c) => !!c.fecha_publicacion),
    [data]
  )

  // Opciones de marca disponibles (derivadas de los datos)
  const marcasDisponibles = useMemo(() => {
    const set = new Set<string>()
    campaniasEnviadas.forEach((c) => {
      const nombre = c.marca_principal_nombre || c.marca
      if (nombre) set.add(nombre)
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [campaniasEnviadas])

  // Campañas tras aplicar el filtro de marca (base para KPIs)
  const campaniasPorMarca = useMemo(() => {
    if (marcaFilter === 'todas') return campaniasEnviadas
    return campaniasEnviadas.filter((c) => (c.marca_principal_nombre || c.marca) === marcaFilter)
  }, [campaniasEnviadas, marcaFilter])

  // KPIs sobre el conjunto filtrado por marca
  const stats = useMemo(() => {
    const counts = { total: 0, recopilando: 0, por_generar: 0, generado: 0 }
    campaniasPorMarca.forEach((c) => {
      counts.total += 1
      const estado = getEstadoReporte(c)
      if (estado === 'recopilando') counts.recopilando += 1
      else if (estado === 'por_generar') counts.por_generar += 1
      else if (estado === 'generado') counts.generado += 1
    })
    return counts
  }, [campaniasPorMarca])

  // Filas de la tabla: marca + estado + estado de actualización
  const campaniasFiltradas = useMemo(() => {
    let rows = campaniasPorMarca
    if (estadoFilter !== 'todos') {
      rows = rows.filter((c) => getEstadoReporte(c) === estadoFilter)
    }
    // El filtro de actualización solo aplica a filas con reporte (reporte_actualizado != null)
    if (showActualizado && actualizadoFilter !== 'todos') {
      rows = rows.filter((c) =>
        actualizadoFilter === 'actualizado'
          ? c.reporte_actualizado === true
          : c.reporte_actualizado === false
      )
    }
    return rows
  }, [campaniasPorMarca, estadoFilter, actualizadoFilter, showActualizado])

  const handleViewDetails = (id: number) => {
    navigate(`/multichannel/campania/${id}`)
  }

  const handleViewReport = (campania: Campania) => {
    const { showEmail, showWhatsApp } = getReportTypes(campania.tipo || '')
    if (showWhatsApp && !showEmail) {
      window.open(`/multichannel/reporte-whatsapp/${campania.id}`, '_blank')
    } else {
      window.open(`/multichannel/reporte/${campania.id}`, '_blank')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-black dark:text-white" />
            Analytics de Campañas
          </h1>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            <Info className="h-5 w-5" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
              Estado de reporte de todas las campañas enviadas
            </span>
          )}
        </div>
        <Button
          variant="outline"
          className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
          onClick={() => navigate('/multichannel/seguimiento')}
        >
          <Send className="mr-2 h-4 w-4" />
          Seguimiento
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {STAT_CARDS.map(({ key, label, icon: Icon, hex }) => {
          const isEstadoCard = key !== 'total'
          const active = estadoFilter === key
          return (
            <button
              key={key}
              type="button"
              onClick={() =>
                isEstadoCard
                  ? setEstadoFilter(active ? 'todos' : (key as EstadoKey))
                  : setEstadoFilter('todos')
              }
              className={`rounded-lg border overflow-hidden text-left transition-all ${
                active ? 'border-gray-400 dark:border-gray-500 ring-1 ring-gray-300 dark:ring-gray-600' : 'border-gray-200 dark:border-gray-700'
              }`}
              style={{ background: `linear-gradient(to right, ${hex}20, transparent 60%)` }}
            >
              <div className="px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
                  <div className="rounded-full p-1.5" style={{ backgroundColor: `${hex}15` }}>
                    <Icon className="h-4 w-4" style={{ color: hex }} />
                  </div>
                </div>
                <span className="text-2xl font-bold text-foreground leading-tight">{stats[key]}</span>
              </div>
            </button>
          )
        })}
      </div>

      {/* Tabla */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center gap-3">
            Campañas
            <Badge className="badge-gradient hover:opacity-90">{campaniasFiltradas.length}</Badge>
          </CardTitle>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select value={estadoFilter} onValueChange={(v) => setEstadoFilter(v as 'todos' | EstadoKey)}>
              <SelectTrigger className="w-full sm:w-[220px] h-9">
                <SelectValue placeholder="Estado de reporte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los estados</SelectItem>
                <SelectItem value="recopilando">Recopilando información</SelectItem>
                <SelectItem value="por_generar">Reporte por generar</SelectItem>
                <SelectItem value="generado">Generado</SelectItem>
                <SelectItem value="sin_estado">Sin estado</SelectItem>
              </SelectContent>
            </Select>
            <Select value={marcaFilter} onValueChange={setMarcaFilter}>
              <SelectTrigger className="w-full sm:w-[220px] h-9">
                <SelectValue placeholder="Marca" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas las marcas</SelectItem>
                {marcasDisponibles.map((marca) => (
                  <SelectItem key={marca} value={marca}>
                    {marca}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {showActualizado && (
              <Select
                value={actualizadoFilter}
                onValueChange={(v) => setActualizadoFilter(v as 'todos' | 'actualizado' | 'pendiente')}
              >
                <SelectTrigger className="w-full sm:w-[220px] h-9">
                  <SelectValue placeholder="Actualización" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Toda actualización</SelectItem>
                  <SelectItem value="actualizado">Actualizado</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : campaniasFiltradas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-lg font-medium text-muted-foreground">No se encontraron campañas</p>
              <p className="text-sm text-muted-foreground">Ajusta los filtros para ver resultados</p>
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-card overflow-hidden">
              <Table>
                <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)]">
                  <TableRow>
                    <TableHead>Campaña</TableHead>
                    <TableHead>Marca</TableHead>
                    <TableHead>Fecha de envío</TableHead>
                    <TableHead>Estado de Reporte</TableHead>
                    {showActualizado && <TableHead>Actualizado</TableHead>}
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaniasFiltradas.map((campania) => {
                    const estado = getEstadoReporte(campania)
                    const colorNombre = campania.marca_color_nombre
                    const colorTono = campania.marca_color_tono || '500'
                    const labName = campania.marca_principal_nombre || campania.marca
                    return (
                      <TableRow key={campania.id} className="hover:bg-muted/50">
                        <TableCell className="font-medium">
                          <div className="max-w-[260px] truncate">{campania.nombre_campania}</div>
                          {campania.target && (
                            <div className="text-[11px] text-muted-foreground font-normal truncate max-w-[260px]">
                              {campania.target}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`h-2.5 w-2.5 rounded-full shrink-0 ${
                                colorNombre ? `bg-${colorNombre}-${colorTono}` : 'bg-gray-400'
                              }`}
                            />
                            <span className="text-sm">{labName}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{formatDate(campania.fecha_publicacion)}</div>
                          {campania.hora && (
                            <div className="text-xs text-muted-foreground">{campania.hora.slice(0, 5)}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={ESTADO_CONFIG[estado].badge}>
                            {ESTADO_CONFIG[estado].label}
                          </Badge>
                        </TableCell>
                        {showActualizado && (
                          <TableCell>
                            {campania.reporte_actualizado == null ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : campania.reporte_actualizado ? (
                              <Badge
                                variant="outline"
                                className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:text-emerald-400"
                              >
                                Actualizado
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="bg-amber-500/10 text-amber-600 border-amber-500/30 dark:text-amber-400"
                              >
                                Pendiente
                              </Badge>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDetails(campania.id)}
                              title="Ver detalle"
                            >
                              <Eye className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                            </Button>
                            {estado === 'generado' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewReport(campania)}
                                title="Ver reporte"
                              >
                                <FileBarChart className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                              </Button>
                            )}
                            {showActualizado && estado === 'generado' && (() => {
                              const { showEmail, showWhatsApp } = getReportTypes(campania.tipo || '')
                              return (
                                <>
                                  {showEmail && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEditReport(campania)}
                                      title="Editar reporte email"
                                    >
                                      <Mail className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                                    </Button>
                                  )}
                                  {showWhatsApp && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEditWhatsAppReport(campania)}
                                      title="Editar reporte WhatsApp"
                                    >
                                      <MessageSquare className="h-4 w-4 text-green-600" />
                                    </Button>
                                  )}
                                </>
                              )
                            })()}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Reporte Email */}
      {selectedCampania && (
        <ReporteModal
          open={reporteEmailModalOpen}
          onClose={() => {
            setReporteEmailModalOpen(false)
            if (!reporteWhatsAppModalOpen) {
              setSelectedCampania(null)
            }
          }}
          campaniaId={selectedCampania.id}
          nombreCampania={selectedCampania.nombre_campania}
          tieneReporte={selectedCampania.estado_reporte === 'Generado'}
        />
      )}

      {/* Modal de Reporte WhatsApp */}
      {selectedCampania && (
        <ReporteWhatsAppModal
          open={reporteWhatsAppModalOpen}
          onClose={() => {
            setReporteWhatsAppModalOpen(false)
            if (!reporteEmailModalOpen) {
              setSelectedCampania(null)
            }
          }}
          campaniaId={selectedCampania.id}
          nombreCampania={selectedCampania.nombre_campania}
          nombreLaboratorio={selectedCampania.marca}
          fechaPublicacion={selectedCampania.fecha_publicacion}
          tieneReporte={selectedCampania.estado_reporte === 'Generado'}
        />
      )}
    </div>
  )
}
